import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import type { NostrEvent } from '@nostrify/nostrify';

export interface LiveStreamEvent extends NostrEvent {
  kind: 30311;
}

export function getLiveTag(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([t]) => t === name)?.[1];
}

export function getLiveTags(event: NostrEvent, name: string): string[] {
  return event.tags.filter(([t]) => t === name).map(([, v]) => v).filter(Boolean);
}

export function isLive(event: NostrEvent): boolean {
  return getLiveTag(event, 'status') === 'live';
}

export function isValidLiveStream(event: NostrEvent): boolean {
  const streaming = getLiveTag(event, 'streaming');
  const title = getLiveTag(event, 'title');
  return !!(streaming && title);
}

/**
 * Deduplicates live stream events by addressable coordinate (pubkey:kind:d).
 * Keeps only the most recently updated event per stream.
 */
export function deduplicateLiveStreams(events: NostrEvent[]): NostrEvent[] {
  const byCoord = new Map<string, NostrEvent>();
  for (const event of events) {
    const d = getLiveTag(event, 'd') ?? '';
    const coord = `${event.pubkey}:${event.kind}:${d}`;
    const existing = byCoord.get(coord);
    if (!existing || event.created_at > existing.created_at) {
      byCoord.set(coord, event);
    }
  }
  return Array.from(byCoord.values());
}

/** Fetch currently live streams (kind 30311 with status=live). */
export function useLiveStreams() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['live-streams'],
    queryFn: async (ctx) => {
      const events = await nostr.query(
        [{ kinds: [30311], limit: 50 }],
        { signal: ctx.signal },
      );
      return deduplicateLiveStreams(events)
        .filter(isLive)
        .filter(isValidLiveStream) as LiveStreamEvent[];
    },
    // Refresh every 30 seconds — live events update frequently
    staleTime: 30000,
    refetchInterval: 30000,
  });
}

/** Fetch all streams by a specific author (live, ended, planned). */
export function useLiveStreamsByAuthor(pubkey: string) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['live-streams-author', pubkey],
    queryFn: async (ctx) => {
      const events = await nostr.query(
        [{ kinds: [30311], authors: [pubkey], limit: 20 }],
        { signal: ctx.signal },
      );
      return deduplicateLiveStreams(events) as LiveStreamEvent[];
    },
    staleTime: 30000,
    enabled: !!pubkey,
  });
}

/** Fetch a single live stream event by naddr components. */
export function useLiveStreamEvent(pubkey: string, dTag: string) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['live-stream-event', pubkey, dTag],
    queryFn: async (ctx) => {
      const events = await nostr.query(
        [{ kinds: [30311], authors: [pubkey], '#d': [dTag], limit: 1 }],
        { signal: ctx.signal },
      );
      return (events[0] ?? null) as LiveStreamEvent | null;
    },
    staleTime: 15000,
    refetchInterval: 15000,
    enabled: !!(pubkey && dTag),
  });
}

export interface GoLiveOptions {
  title: string;
  summary: string;
  streamingUrl: string;
  imageUrl?: string;
  tags?: string[];
  dTag?: string;
}

/** Hook that returns a function to publish / update a kind 30311 live stream event. */
export function useGoLive() {
  const { mutate: publishEvent, isPending, isSuccess, isError, error } = useNostrPublish();

  const goLive = (opts: GoLiveOptions, callbacks?: { onSuccess?: () => void; onError?: (e: Error) => void }) => {
    const now = Math.floor(Date.now() / 1000);
    const d = opts.dTag ?? `stream-${now}`;

    const eventTags: string[][] = [
      ['d', d],
      ['title', opts.title],
      ['streaming', opts.streamingUrl],
      ['starts', String(now)],
      ['status', 'live'],
    ];

    if (opts.summary) eventTags.push(['summary', opts.summary]);
    if (opts.imageUrl) eventTags.push(['image', opts.imageUrl]);
    opts.tags?.forEach(t => eventTags.push(['t', t.toLowerCase().trim()]));

    publishEvent(
      { kind: 30311, content: '', tags: eventTags },
      {
        onSuccess: callbacks?.onSuccess,
        onError: callbacks?.onError,
      },
    );
  };

  const endStream = (dTag: string, pubkey: string, callbacks?: { onSuccess?: () => void }) => {
    const now = Math.floor(Date.now() / 1000);
    publishEvent(
      {
        kind: 30311,
        content: '',
        tags: [
          ['d', dTag],
          ['status', 'ended'],
          ['ends', String(now)],
        ],
      },
      { onSuccess: callbacks?.onSuccess },
    );
  };

  return { goLive, endStream, isPending, isSuccess, isError, error };
}
